import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { BadgeEtape } from "./badges";
import type { CarteScout, Pari } from "./contract";
import { euros, formatDate, scoreEnClair } from "./format";
import { useScout } from "./queries";
import { ConsoleShell } from "./shell";
import { Chargement, EcranDErreur } from "./states";

/**
 * Le detail d'une opportunite du scout : la carte entiere, telle que lab-tools la rend.
 *
 * Deux champs peuvent manquer tant que `GET /opportunities` ne les rend pas (`resume` et
 * `coutEstimeCents`, piege connu du ticket 2) : la case reste vide, la page ne tombe pas.
 * Le jour ou lab-tools les ajoute, il n'y a rien a changer ici.
 *
 * Les liens de la carte (`ticket`, `preuves[].url`) viennent du scout, qui ingere de la
 * veille : ce sont des textes influencables. Ils ne deviennent une ancre que s'ils partent
 * sur le web ; tout autre schema (`javascript:`, `data:`, un chemin) reste du texte.
 */

/** L'adresse telle quelle si elle part sur le web, sinon null. */
export function sortant(url: string | null): string | null {
  return url !== null && /^https?:\/\//i.test(url) ? url : null;
}

/** Un lien qui sort de la console, ou son texte seul quand `sortant` le refuse. */
function LienSortant({ texte, url }: { texte: string; url: string | null }) {
  const cible = sortant(url);
  if (cible === null) return <>{texte}</>;
  return (
    <a
      className="underline underline-offset-2"
      href={cible}
      rel="noopener noreferrer"
      target="_blank"
    >
      {texte}
    </a>
  );
}

export function ScoutPage({ id }: { id: string }) {
  const scout = useScout(id);

  return (
    <ConsoleShell connecte={scout.isSuccess}>
      <p className="text-sm text-muted-foreground">
        <Link className="underline underline-offset-2" to="/">
          Portefeuille
        </Link>{" "}
        / scout {id}
      </p>
      {scout.isPending ? <Chargement quoi="de l'opportunité" /> : null}
      {scout.isError ? <EcranDErreur error={scout.error} quoi={`l'opportunité ${id}`} /> : null}
      {scout.data ? <Carte carte={scout.data.carte} pari={scout.data.pari} /> : null}
    </ConsoleShell>
  );
}

function Carte({ carte, pari }: { carte: CarteScout; pari: Pari }) {
  return (
    <>
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold">{carte.titre}</h1>
        <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <BadgeEtape etape={pari.etape} />
          <span>scout {carte.identifiantCourt}</span>
          {carte.piste ? <span>piste {carte.piste}</span> : null}
          {carte.canal ? <span>canal {carte.canal}</span> : null}
          {carte.niche ? <span>niche {carte.niche}</span> : null}
          {carte.origine ? <span>origine {carte.origine}</span> : null}
          <span>statut {carte.statut}</span>
          {carte.trouveLe ? <span>découverte le {formatDate(carte.trouveLe)}</span> : null}
        </p>
      </header>

      <section className="grid gap-4 md:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle>{scoreEnClair(carte.score, null) || "score non enregistré"}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-col gap-2 text-sm">
              {carte.grille.map((critere) => (
                <li
                  className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)_4rem] items-center gap-3"
                  key={critere.critere}
                >
                  <span>{critere.critere}</span>
                  <Jauge maximum={critere.maximum} valeur={critere.valeur} />
                  <span className="whitespace-nowrap text-right text-muted-foreground">
                    {critere.valeur === null ? "non noté" : `${critere.valeur}/${critere.maximum}`}
                  </span>
                </li>
              ))}
            </ul>
            <p className="pt-3 text-sm text-muted-foreground">
              Plafond non enregistré : le scout ne le garde pas.
            </p>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-4">
          <Card>
            <CardContent className="flex flex-col gap-4 pt-6">
              <Champ libelle="Décision">
                {[carte.decision, carte.decideLe ? `le ${formatDate(carte.decideLe)}` : null]
                  .filter((morceau) => morceau !== null)
                  .join(", ") || "aucune"}
              </Champ>
              <Champ libelle="Remarque">{carte.remarque ?? ""}</Champ>
              <div className="grid gap-4 sm:grid-cols-3">
                <Champ libelle="Ticket">
                  {carte.ticket ? <LienSortant texte={carte.ticket} url={carte.ticket} /> : ""}
                </Champ>
                <Champ libelle="Cadrage">{carte.cadrage ?? ""}</Champ>
                <Champ libelle="Coût estimé">
                  {carte.coutEstimeCents === null ? <NonRendu /> : euros(carte.coutEstimeCents)}
                </Champ>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex flex-col gap-4 pt-6">
              <Champ libelle="Résumé">{carte.resume ?? <NonRendu />}</Champ>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase text-muted-foreground">
                  Preuves
                </span>
                {carte.preuves.length === 0 ? (
                  <span className="text-sm text-muted-foreground">Aucune preuve enregistrée.</span>
                ) : (
                  <ul className="flex flex-col gap-1 text-sm">
                    {carte.preuves.map((preuve) => (
                      <li key={`${preuve.texte}-${preuve.url ?? ""}`}>
                        <LienSortant texte={preuve.texte} url={preuve.url} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="grid gap-4 pt-6 sm:grid-cols-2">
              <Champ libelle="Plan de test">{carte.planDeTest ?? ""}</Champ>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase text-muted-foreground">
                  Risques
                </span>
                {carte.risques.length === 0 ? (
                  <span className="text-sm text-muted-foreground">Aucun risque enregistré.</span>
                ) : (
                  <ul className="flex list-disc flex-col gap-1 pl-4 text-sm">
                    {carte.risques.map((risque) => (
                      <li key={risque}>{risque}</li>
                    ))}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </>
  );
}

function Champ({ libelle, children }: { libelle: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase text-muted-foreground">{libelle}</span>
      <span className="text-sm">{children}</span>
    </div>
  );
}

/**
 * Ce que lab-tools ne rend pas encore (`resume`, `estimated_cost_cents`) : une case vide
 * qui dit pourquoi, plutot qu'un blanc qu'on prendrait pour une panne de la page.
 */
function NonRendu() {
  return <span className="text-muted-foreground">non rendu par lab-tools</span>;
}

/** La note d'un critere, en barre. Un critere non note laisse la barre vide. */
function Jauge({ valeur, maximum }: { valeur: number | null; maximum: number }) {
  const part = valeur === null || maximum <= 0 ? 0 : Math.min(1, Math.max(0, valeur / maximum));
  return (
    <span className="block h-2 overflow-hidden rounded-full bg-muted">
      <span className="block h-full bg-primary" style={{ width: `${part * 100}%` }} />
    </span>
  );
}
